import sumBy from 'lodash/sumBy'
import moment from 'moment'
import React from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import LegendComponent from '../../../common/legend/Legend'
import { i18n } from '../../index'
import css from '../incentiveSales/incentiveSales.css'
import ChartLegend from './ChartLegend'
import { BudgetItem } from './TotalSalesTable'
type Props = {
  month: number
  year: number
  data: { budget: BudgetItem[] }
}
const STROKES = {
  ACTUAL: '#78A22F',
  TARGET: '#f06767',
  PREVIOUS: '#F7AC01',
}

type State = {}

class TotalSalesGraph extends React.Component<Props, State> {
  renderWeekAreas = () => {
    const {
      data: { budget = [] },
    } = this.props
    let firstDayOfWeek, lastDayOfWeek, week
    const areas: JSX.Element[] = []
    budget.forEach((obj, idx) => {
      const date = obj.date
      const weekOfDate = moment(date).week()

      if (!week) {
        week = weekOfDate
        firstDayOfWeek = date
      } else {
        if (weekOfDate === week) {
          lastDayOfWeek = date
        } else {
          areas.push(
            <ReferenceArea
              key={firstDayOfWeek}
              x1={firstDayOfWeek}
              x2={lastDayOfWeek}
              ifOverflow='extendDomain'
              shape={OffsetShape}
            />,
          )
          week = weekOfDate
          firstDayOfWeek = date
          lastDayOfWeek = undefined
        }
      }

      if (idx === budget.length - 1) {
        areas.push(
          <ReferenceArea
            key={firstDayOfWeek}
            x1={firstDayOfWeek}
            x2={lastDayOfWeek}
            ifOverflow='extendDomain'
            shape={OffsetShape}
          />,
        )
      }
    })
    return areas
  }

  render() {
    const { month, year, data } = this.props
    const displayedMonth = moment()
      .year(year)
      .month(month - 1)
      .format('YYYY-MM')
    const date = moment()
    let legendComponent = <LegendComponent />

    if (displayedMonth === date.format('YYYY-MM')) {
      const cumulativeData = data.budget.slice(0, date.date())
      const processedData = {
        [i18n.t('reporting.budget')?.toString() ?? '']: `${sumBy(cumulativeData, 'budget')}€`,
        [i18n.t('reporting.actualLastYear')?.toString() ?? '']: `${sumBy(cumulativeData, 'totalSalesLastYear')}€`,
        [i18n.t('reporting.actualSales')?.toString() ?? '']: `${sumBy(cumulativeData, 'totalSales')}€`,
      }
      legendComponent = <ChartLegend additionalValues={processedData} />
    }

    return (
      <div>
        <h4 className={css.month}>
          {moment()
            .year(year)
            .month(month - 1)
            .format('MMMM YYYY')}
        </h4>
        <div className={css.chart}>
          <div>
            <ResponsiveContainer width='100%' height={300}>
              <ComposedChart
                margin={{
                  top: 30,
                  right: 10,
                  bottom: 10,
                  left: 10,
                }}
                data={data.budget}
              >
                <CartesianGrid />

                <XAxis
                  dataKey={'date'}
                  interval={0}
                  tickFormatter={(item) => moment(item).format('D')}
                  axisLine={{
                    stroke: '#ccc',
                    strokeWidth: 1,
                  }}
                  tickSize={0}
                  tickMargin={10}
                  tick={{
                    fontSize: 12,
                  }}
                />
                <YAxis
                  padding={{
                    top: 0,
                  }}
                  axisLine={{
                    stroke: '#ccc',
                    strokeWidth: 1,
                  }}
                  tickCount={6}
                  tickSize={0}
                  tickMargin={10}
                  tick={{
                    fontSize: 12,
                  }}
                >
                  <Label position='top' offset={15} />
                </YAxis>

                <Tooltip />

                <Legend content={legendComponent} />

                <Line
                  type='linear'
                  dataKey={'budget'}
                  dot={false}
                  name={i18n.t('reporting.budget')}
                  legendType='line'
                  strokeWidth={3}
                  stroke={STROKES.TARGET}
                />
                <Line
                  type='linear'
                  dataKey={'totalSalesLastYear'}
                  dot={false}
                  name={i18n.t('reporting.actualLastYear')}
                  legendType='line'
                  strokeWidth={3}
                  stroke={STROKES.PREVIOUS}
                />

                <Bar
                  dataKey={'totalSales'}
                  name={i18n.t('reporting.actualSales')?.toString()}
                  barSize={10}
                  fill={STROKES.ACTUAL}
                />

                {this.renderWeekAreas()}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    )
  }
} // Default ReferenceAreas would not line up with the charts so needed to make a custom shape with x offset of 17

const OffsetShape = (props) => {
  const x = props.x
  const y = props.y
  const w = props.width
  const h = props.height
  const d = `M ${x},${y} h ${w} v ${h} h ${-w} Z`
  return (
    <g>
      <path
        r={props.r}
        fill='#ccc'
        fillOpacity='0.2'
        stroke='none'
        strokeWidth='1'
        radius='0'
        className='recharts-rectangle recharts-reference-area-rect'
        d={d}
      />
    </g>
  )
}

export default TotalSalesGraph
